'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const hours = ['08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21'];

// Backend integration point: GET /api/hotels/:id/courts/schedule?date=...
const scheduleData: Record<string, { id: string; guest: string; flag: string; duration: number; startHour: number; status: string }[]> = {
  'Court 1': [
    { id: 'slot-c1-1', guest: 'Thomas M.', flag: '🇩🇪', duration: 1, startHour: 8, status: 'confirmed' },
    { id: 'slot-c1-2', guest: 'Marie D.', flag: '🇫🇷', duration: 1.5, startHour: 10, status: 'confirmed' },
    { id: 'slot-c1-3', guest: 'BLOCKED', flag: '', duration: 1, startHour: 13, status: 'blocked' },
    { id: 'slot-c1-4', guest: 'Sophie L.', flag: '🇫🇷', duration: 1, startHour: 16, status: 'pending' },
    { id: 'slot-c1-5', guest: 'Lena S.', flag: '🇩🇪', duration: 2, startHour: 19, status: 'confirmed' },
  ],
  'Court 2': [
    { id: 'slot-c2-1', guest: 'James W.', flag: '🇬🇧', duration: 1, startHour: 9, status: 'confirmed' },
    { id: 'slot-c2-2', guest: 'Anna K.', flag: '🇵🇱', duration: 1.5, startHour: 14, status: 'pending' },
    { id: 'slot-c2-3', guest: 'Carlos R.', flag: '🇪🇸', duration: 1, startHour: 18, status: 'confirmed' },
  ],
  'Court 3': [
    { id: 'slot-c3-1', guest: 'Karim B.', flag: '🇹🇳', duration: 2, startHour: 8, status: 'confirmed' },
    { id: 'slot-c3-2', guest: 'Emma P.', flag: '🇫🇷', duration: 1, startHour: 11, status: 'confirmed' },
    { id: 'slot-c3-3', guest: 'Marco B.', flag: '🇮🇹', duration: 1, startHour: 18, status: 'confirmed' },
    { id: 'slot-c3-4', guest: 'Lisa H.', flag: '🇩🇪', duration: 1.5, startHour: 20, status: 'confirmed' },
  ],
};

const statusColors: Record<string, string> = {
  confirmed: 'bg-primary/15 border-primary/30 text-primary',
  pending: 'bg-amber-50 border-amber-200 text-amber-700',
  blocked: 'bg-muted border-border text-muted-foreground',
};

export default function CourtTimeline() {
  const [selectedDate, setSelectedDate] = useState('19 Apr');
  const totalSlots = 14;
  const hourWidth = 100 / totalSlots;

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Today's Schedule</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Sat, 19 Apr 2026</p>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <ChevronLeft size={15} className="text-muted-foreground" />
          </button>
          <span className="text-xs font-medium text-foreground px-2">{selectedDate}</span>
          <button className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <ChevronRight size={15} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="min-w-[480px]">
          {/* Hour labels */}
          <div className="flex mb-1 ml-16">
            {hours.map((h) => (
              <div key={`hour-label-${h}`} className="flex-1 text-center">
                <span className="text-xs text-muted-foreground font-mono">{h}</span>
              </div>
            ))}
          </div>

          {/* Court rows */}
          {Object.entries(scheduleData).map(([court, slots]) => (
            <div key={`timeline-row-${court}`} className="flex items-center mb-2 gap-2">
              <div className="w-14 flex-shrink-0">
                <span className="text-xs font-semibold text-foreground">{court}</span>
              </div>
              <div className="flex-1 relative h-10 bg-muted/40 rounded-lg overflow-hidden">
                {/* Hour grid lines */}
                {hours.map((h, i) => (
                  <div
                    key={`grid-${court}-${h}`}
                    className="absolute top-0 bottom-0 border-l border-border/40"
                    style={{ left: `${(i / totalSlots) * 100}%` }}
                  />
                ))}
                {/* Booked slots */}
                {slots.map((slot) => {
                  const startPct = ((slot.startHour - 8) / totalSlots) * 100;
                  const widthPct = (slot.duration / totalSlots) * 100;
                  return (
                    <div
                      key={slot.id}
                      className={`absolute top-1 bottom-1 rounded-md border text-xs flex items-center px-1.5 overflow-hidden cursor-pointer hover:brightness-95 transition-all ${statusColors[slot.status]}`}
                      style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                      title={`${slot.guest} · ${slot.startHour}:00 · ${slot.duration}h`}
                    >
                      <span className="truncate font-medium text-[10px]">
                        {slot.flag} {slot.guest}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border">
        {[
          { label: 'Confirmed', color: 'bg-primary/20 border-primary/30' },
          { label: 'Pending', color: 'bg-amber-100 border-amber-200' },
          { label: 'Blocked', color: 'bg-muted border-border' },
        ].map((item) => (
          <div key={`legend-${item.label}`} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm border ${item.color}`} />
            <span className="text-xs text-muted-foreground">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {[
          { label: 'Booked', value: '11', color: 'text-primary' },
          { label: 'Available', value: '25', color: 'text-green-600' },
          { label: 'Blocked', value: '1', color: 'text-muted-foreground' },
        ].map((s) => (
          <div key={`qs-${s.label}`} className="text-center p-2 rounded-xl bg-muted/40">
            <p className={`text-lg font-bold font-mono tabular-nums ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}